import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

export const useVendors = () => {
  const [v, setV] = useState([]);
  useEffect(() => { api.get("/suppliers").then((r) => setV(r.data)).catch(() => setV([])); }, []);
  return v;
};

export const VendorDialog = ({ open, onOpenChange, vendor, initialName = "", onSaved }) => {
  const [f, setF] = useState({ name: "", address: "", gstin: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ name: vendor?.name || initialName, address: vendor?.address || "", gstin: vendor?.gstin || "" }); }, [open, vendor, initialName]);
  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = vendor ? await api.put(`/suppliers/${vendor.id}`, f) : await api.post("/suppliers", f);
      toast.success(vendor ? "Vendor updated" : "Vendor added");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="vendor-dialog">
        <DialogHeader><DialogTitle>{vendor ? "Edit vendor" : "Add new vendor"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="space-y-1.5"><Label>Vendor / supplier name</Label><Input required value={f.name} onChange={set("name")} data-testid="vendor-name-input" /></div>
          <div className="space-y-1.5"><Label>Address</Label><Input value={f.address} onChange={set("address")} data-testid="vendor-address-input" /></div>
          <div className="space-y-1.5"><Label>GSTIN</Label><Input value={f.gstin} onChange={set("gstin")} data-testid="vendor-gstin-input" /></div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="vendor-save-btn">{busy ? "Saving…" : "Save vendor"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const VendorPicker = ({ value, onPick, testid = "vendor-picker", placeholder = "Pick a vendor" }) => {
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [text, setText] = useState("");
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/suppliers").then((r) => setVendors(r.data)).catch(() => setVendors([]));
  useEffect(() => { load(); }, []);
  const pick = (v) => { onPick(v); setOpen(false); setText(""); };
  return (
    <>
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid} className="flex min-h-9 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-left text-sm hover:border-primary/60 transition-colors">
            <span className={cn("product-name", !value && "text-muted-foreground")}>{value || placeholder}</span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[280px] p-0" align="start">
          <Command>
            <CommandInput value={text} onValueChange={setText} placeholder="Search vendors…" data-testid={`${testid}-search`} />
            <CommandList className="max-h-64">
              <CommandEmpty>No saved vendor matches</CommandEmpty>
              <CommandGroup>
                <CommandItem value={`__add__ ${text}`} onSelect={() => { setOpen(false); setAdding(true); }} data-testid={`${testid}-add`}>
                  <Plus className="mr-2 h-4 w-4 text-primary" /> Add new vendor{text.trim() ? ` “${text.trim()}”` : ""}
                </CommandItem>
              </CommandGroup>
              <CommandGroup>
                {vendors.map((v) => (
                  <CommandItem key={v.id} value={`${v.name} ${v.gstin}`} onSelect={() => pick(v)} data-testid={`${testid}-option-${v.name_key}`}>
                    <Check className={cn("mr-2 h-4 w-4 shrink-0", value === v.name ? "opacity-100 text-primary" : "opacity-0")} />
                    <span className="product-name">{v.name}{v.gstin && <span className="ml-2 font-mono text-[11px] text-muted-foreground">{v.gstin}</span>}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <VendorDialog open={adding} onOpenChange={setAdding} initialName={text.trim()} onSaved={(v) => { load(); pick(v); }} />
    </>
  );
};
